import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { apiPost } from '@/utils/api';

function VerificationPending() {
  const location = useLocation();
  const { email, name } = location.state || {};
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    if (!email) return;
    
    setResending(true);
    setError('');

    try {
      const response = await apiPost('/auth/resend-verification', { email });

      if (response.ok) {
        setResent(true);
        setTimeout(() => setResent(false), 30000); // Reset after 30 seconds
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to resend verification email');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-100 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
            <span className="text-5xl">📧</span>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm">✓</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email!</h1>
          <p className="text-gray-600">
            {name ? `Hi ${name}! ` : ''}We&apos;ve sent a verification link to:
          </p>
          {email && (
            <p className="font-semibold text-emerald-600 mt-2 text-lg">{email}</p>
          )}
        </div>

        {/* Steps */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">📋 Next Steps:</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
              <div>
                <p className="font-medium text-gray-900">Check your inbox</p>
                <p className="text-sm text-gray-600">Look for an email from GuapGoals</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
              <div>
                <p className="font-medium text-gray-900">Click the verification link</p>
                <p className="text-sm text-gray-600">This confirms your email address</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
              <div>
                <p className="font-medium text-gray-900">Complete 2FA setup</p>
                <p className="text-sm text-gray-600">Enter the security code we&apos;ll send you</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-gray-300 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">4</div>
              <div>
                <p className="font-medium text-gray-400">Start using GuapGoals!</p>
                <p className="text-sm text-gray-400">Set up your budget and goals</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Resend Section */}
        <div className="text-center">
          <p className="text-gray-600 text-sm mb-3">
            Didn&apos;t receive the email? Check your spam folder or:
          </p>
          {resent ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-green-600 text-sm">
                ✅ Verification email sent! Please check your inbox.
              </p>
            </div>
          ) : (
            <button
              onClick={handleResend}
              disabled={resending || !email}
              className={`w-full py-3 rounded-lg font-semibold transition-all ${
                resending || !email
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              }`}
            >
              {resending ? 'Sending...' : '📨 Resend Verification Email'}
            </button>
          )}
        </div>

        {/* Back to Login */}
        <div className="mt-6 pt-6 border-t border-gray-200 text-center">
          <p className="text-gray-600 text-sm mb-2">Already verified?</p>
          <Link
            to="/app/login"
            className="text-emerald-600 hover:text-emerald-800 font-medium"
          >
            ← Back to Login
          </Link>
        </div>

        {/* Security Info */}
        <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-blue-600">🔐</span>
            <span className="font-semibold text-blue-800 text-sm">Why Verify Your Email?</span>
          </div>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Protects your account from unauthorized access</li>
            <li>• Enables 2-Factor Authentication for extra security</li>
            <li>• Allows password recovery if you ever forget it</li>
            <li>• Ensures you receive important budget reminders</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default VerificationPending;
